(async () => {
    const { Core } = await importModule('Core');
    const PREFIX_BUY_WITH = 'buy_with_';
    const LINE_ITEM_PROPS_REGEXP = /(?<=\[).+?(?=\])/;
    const CONFIGURATOR_SELECTOR = '[data-pb-configurator][data-ready="1"]';
    const CONFIGURATOR_DATA_SELECTOR = '[data-pb-data]';
    const PROP_MODE = '_pb_mode';
    const PROP_CITY = 'Ciudad';
    const PROP_PACKAGES = 'Cantidad de paquetes';

    const normalize = (value) => String(value ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .replace(/\s+/g, ' ')
        .toLowerCase();

    const positiveInteger = (value) => {
        const parsed = Number.parseInt(String(value ?? '').replace(/[^\d]/g, ''), 10);
        return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
    };

    const parseRange = (label) => {
        const text = String(label ?? '').trim();
        const values = text.match(/\d[\d.,\s]*/g) || [];
        const from = positiveInteger(values[0]);
        const open = /o\s*m[aá]s|en\s*adelante|desde\s+ah[ií]|\+|mayor(?:es)?/i.test(text);
        const to = open ? null : positiveInteger(values[1]);

        return {
            valid: Boolean(text)
                && Number.isInteger(from)
                && from > 0
                && (open || (Number.isInteger(to) && to >= from))
        };
    };

    const hashString = (value) => {
        let hash = 2166136261;
        const text = String(value ?? '');
        for (let index = 0; index < text.length; index += 1) {
            hash ^= text.charCodeAt(index);
            hash = Math.imul(hash, 16777619);
        }
        return (hash >>> 0).toString(36);
    };

    customElements.define('product-form', class extends Core {
        elements = {
            formTargeter: '[data-product-form-targeter]',
            toCartButton: 'product-to-cart'
        };

        render() {
            if (!this.$formTargeter) return;
            this.$form = this.$formTargeter.form;
            this.$form?.addEventListener('submit', this._submitHandler.bind(this));
        }

        _submitHandler(event) {
            if (event.defaultPrevented) return;
            event.preventDefault();

            this._preparePuraboxProperties();
            const items = this.cartItems;
            this.$toCartButton.addToCart(items, this.mainVariantId);
        }

        _isVariantInput([inputName, value]) {
            if (inputName === 'selling_plan') this._setSellingPlan(value);
            return inputName.includes(PREFIX_BUY_WITH) || inputName === 'id';
        }

        _setSellingPlan(id) {
            this.sellingPlanId = id;
        }

        _setCartItem([inputName, value]) {
            if (inputName === 'id') this.mainVariantId = value;
            const sellingPlanId = this.sellingPlanId || '';

            return {
                id: value,
                quantity: this._getItemQty(inputName),
                selling_plan: inputName === 'id' ? sellingPlanId : '',
                properties: this._getProperties(inputName)
            };
        }

        _getItemQty(inputName) {
            return inputName === 'id' ? this.qty : 1;
        }

        _getProperties(inputName) {
            return inputName === 'id' ? this.properties : {};
        }

        _getConfiguratorRoot() {
            const section = this.closest('.shopify-section');
            return section?.querySelector(CONFIGURATOR_SELECTOR)
                || document.querySelector(CONFIGURATOR_SELECTOR);
        }

        _getQuantityOption(config) {
            const optionNames = Array.isArray(config.optionNames)
                ? config.optionNames
                : [];
            const configuredNames = [
                config.explicitQuantityOption,
                config.legacyQuantityOption
            ].filter(Boolean);

            for (const configuredName of configuredNames) {
                const exact = optionNames.find(
                    (name) => normalize(name) === normalize(configuredName)
                );
                if (exact) return exact;
            }

            const options = Array.isArray(config.options) ? config.options : [];
            const candidates = options.filter((option) => {
                const values = Array.isArray(option.values) ? option.values : [];
                return values.length > 0 && values.every((value) => parseRange(value).valid);
            });

            return candidates.length === 1 ? candidates[0].name : null;
        }

        _setHiddenProperty(name, value) {
            const fieldName = `properties[${name}]`;
            let input = Array.from(this.$form.elements || []).find(
                (field) => field.name === fieldName
            );

            if (!input) {
                input = document.createElement('input');
                input.type = 'hidden';
                input.name = fieldName;
                this.$form.appendChild(input);
            }

            input.value = String(value ?? '');
        }

        _preparePuraboxProperties() {
            const data = this.formData;
            const mode = data.get(`properties[${PROP_MODE}]`);
            if (normalize(mode) !== 'rangos') return;

            const root = this._getConfiguratorRoot();
            const dataNode = root?.querySelector(CONFIGURATOR_DATA_SELECTOR);
            if (!dataNode) return;

            let config;
            try {
                config = JSON.parse(dataNode.textContent || '');
            } catch (error) {
                console.error('Purabox: no se pudo leer la configuración del producto.', error);
                return;
            }

            const productId = String(config.productId ?? '').match(/\d+$/)?.[0];
            const quantityOption = this._getQuantityOption(config);
            const increment = positiveInteger(config.increment);
            const optionNames = Array.isArray(config.optionNames) ? config.optionNames : [];
            const variants = Array.isArray(config.variants) ? config.variants : [];
            const formVariantIds = data.getAll('id')
                .map((id) => String(id ?? ''))
                .filter(Boolean);
            const variantId = String(
                this.$form?.querySelector('[data-pb-cart-variant]')?.value
                || formVariantIds[formVariantIds.length - 1]
                || ''
            );
            const selectedVariant = variants.find(
                ({ id }) => String(id) === variantId
            );

            if (!productId || !quantityOption || !increment || !selectedVariant) return;

            const city = data.get(`properties[${PROP_CITY}]`) || '';
            const parts = [productId, normalize(mode), normalize(city)];

            optionNames.forEach((name, index) => {
                if (normalize(name) !== normalize(quantityOption)) {
                    parts.push(`${normalize(name)}=${normalize(selectedVariant.values?.[index])}`);
                }
            });

            this._setHiddenProperty('_pb_group', `pb1-${hashString(parts.join('\u001f'))}`);
            this._setHiddenProperty('_pb_product_id', productId);
            this._setHiddenProperty('_pb_quantity_option', quantityOption);
            this._setHiddenProperty('_pb_increment', increment);
        }

        get cartItems() {
            let entries = Array.from(this.formData.entries())
                .filter(this._isVariantInput.bind(this));

            const mode = this.formData.get(`properties[${PROP_MODE}]`);
            if (normalize(mode) === 'rangos') {
                const variantEntries = entries.filter(([inputName, value]) => (
                    inputName === 'id' && String(value ?? '')
                ));
                const dedicatedVariantId = this.$form
                    ?.querySelector('[data-pb-cart-variant]')
                    ?.value;
                const targetVariantId = String(
                    dedicatedVariantId
                    || variantEntries[variantEntries.length - 1]?.[1]
                    || ''
                );

                if (targetVariantId) {
                    let mainVariantAdded = false;
                    entries = entries.filter(([inputName, value]) => {
                        if (inputName !== 'id') return true;
                        if (mainVariantAdded || String(value) !== targetVariantId) return false;
                        mainVariantAdded = true;
                        return true;
                    });
                }
            }

            return entries.map(this._setCartItem.bind(this));
        }

        get formData() {
            return new FormData(this.$form);
        }

        get qty() {
            const configuredPackages = positiveInteger(
                this.formData.get(`properties[${PROP_PACKAGES}]`)
            );

            if (configuredPackages) return configuredPackages;
            return +this.formData.get('quantity') || 1;
        }

        get properties() {
            return Array.from(this.formData.entries()).reduce((acc, [key, val]) => {
                if (key.startsWith('properties[') && key.endsWith(']') && val) {
                    const match = key.match(LINE_ITEM_PROPS_REGEXP);
                    if (match?.[0]) acc[match[0]] = val;
                }
                return acc;
            }, {});
        }
    });
})();
